export interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

let installPromptEvent: InstallPromptEvent | null = null;
const installPromptListeners = new Set<(event: InstallPromptEvent | null) => void>();

function publishInstallPrompt(event: InstallPromptEvent | null) {
  installPromptEvent = event;
  for (const listener of installPromptListeners) listener(event);
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    publishInstallPrompt(event as InstallPromptEvent);
  });
  window.addEventListener("appinstalled", () => publishInstallPrompt(null));
}

export function watchInstallPrompt(listener: (event: InstallPromptEvent | null) => void) {
  installPromptListeners.add(listener);
  listener(installPromptEvent);
  return () => { installPromptListeners.delete(listener); };
}

export async function promptPwaInstall() {
  const event = installPromptEvent;
  if (!event) return false;
  await event.prompt();
  const choice = await event.userChoice;
  publishInstallPrompt(null);
  return choice.outcome === "accepted";
}

export function registerPwa(onUpdate: () => void) {
  if (!("serviceWorker" in navigator) || import.meta.env.DEV) return () => undefined;
  let disposed = false;
  navigator.serviceWorker.register("/sw.js").then((registration) => {
    registration.addEventListener("updatefound", () => {
      const installing = registration.installing;
      installing?.addEventListener("statechange", () => {
        if (!disposed && installing.state === "installed" && navigator.serviceWorker.controller) onUpdate();
      });
    });
  }).catch(() => undefined);
  return () => { disposed = true; };
}

export async function keepScreenAwake(): Promise<() => void> {
  if (!("wakeLock" in navigator)) return () => undefined;
  try {
    const sentinel = await navigator.wakeLock.request("screen");
    return () => { void sentinel.release(); };
  } catch {
    return () => undefined;
  }
}
